import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Spinner } from '@/components/Spinner';

interface Props {
  children: ReactNode;
  requireAdmin?: boolean;
  // Where to send logged-out users. Defaults to the login page (preserving
  // the attempted path so we can bounce back after sign-in). Pass a plain
  // path (e.g. '/') for pages that should silently disappear when logged
  // out rather than prompt for login.
  redirectTo?: string;
}

export function RequireAuth({ children, requireAdmin = false, redirectTo }: Props) {
  const user = useCurrentUser();
  const location = useLocation();

  if (user === undefined) {
    return (
      <main className="mx-auto flex min-h-dvh items-center justify-center">
        <Spinner />
      </main>
    );
  }

  if (user === null) {
    if (redirectTo) return <Navigate to={redirectTo} replace />;
    // Bounce to login, preserving where the user was headed so we can
    // redirect back after they sign in.
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (requireAdmin && user.role !== 'admin') {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-2 px-6 text-center">
        <span className="text-4xl" aria-hidden>
          🔒
        </span>
        <h1 className="text-xl font-semibold">Admin access required</h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">
          Only admins can open this page.
        </p>
      </main>
    );
  }

  return <>{children}</>;
}
